import * as React from "react";
import { useState, useEffect } from "react";
import { Field, withTypes } from "react-final-form";
import axios from "axios";

import {
    Button,
    CircularProgress,
    TextField,
    Switch,
    FormControlLabel,
    Typography,
    Select,
    MenuItem,
    FormControl,
    Table,
    TableHead,
    TableBody,
    TableRow,
    TableCell,
    Chip,
    IconButton,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Paper,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import AddIcon from "@material-ui/icons/Add";
import EditIcon from "@material-ui/icons/Edit";
import DeleteIcon from "@material-ui/icons/Delete";

import { Notification, useNotify } from "react-admin";
import { API } from "../Constant";
import { getConfigGet, getConfigPost } from "../getConfig";

const PROMO_BANNER_ENDPOINT = API + "/promo-banner-config";

const useStyles = makeStyles((theme) => ({
    main: {
        display: "flex",
        flexDirection: "column",
        minHeight: "100vh",
        padding: theme.spacing(3),
        backgroundColor: "#f5f5f5",
    },
    header: {
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        marginBottom: theme.spacing(3),
    },
    title: {
        fontWeight: 600,
    },
    formContainer: {
        padding: theme.spacing(3),
        marginBottom: theme.spacing(3),
    },
    fieldGrid: {
        display: "grid",
        gridTemplateColumns: "repeat(3, 1fr)",
        gap: "24px",
        marginBottom: theme.spacing(3),
        [theme.breakpoints.down('md')]: {
            gridTemplateColumns: "repeat(2, 1fr)",
        },
        [theme.breakpoints.down('sm')]: {
            gridTemplateColumns: "1fr",
        },
    },
    sectionTitle: {
        fontWeight: 500,
        marginBottom: theme.spacing(2),
        marginTop: theme.spacing(2),
        color: theme.palette.primary.main,
    },
    tableContainer: {
        marginBottom: theme.spacing(3),
    },
    statusChip: {
        fontWeight: 500,
    },
    activeChip: {
        backgroundColor: "#4caf50",
        color: "white",
    },
    inactiveChip: {
        backgroundColor: "#9e9e9e",
        color: "white",
    },
    buttonGroup: {
        marginTop: theme.spacing(3),
    },
    previewBox: {
        marginTop: theme.spacing(2),
        padding: theme.spacing(2),
        backgroundColor: "#f0f0f0",
        borderRadius: 8,
    },
    previewImage: {
        maxWidth: "100%",
        maxHeight: 200,
        objectFit: "contain",
        borderRadius: 8,
    },
}));

// Form types
interface BannerFormData {
    name?: string;
    is_active?: boolean;
    priority?: string;
    target_user_type?: string;
    target_user_ids?: string;
    // Banner Config
    banner_image?: string;
    banner_height?: string;
    banner_width?: string;
    // Click Action
    click_action?: string;
    click_url?: string;
}

interface BannerRecord {
    id: number;
    name: string;
    is_active: boolean;
    priority: number;
    target_user_type: string;
    target_user_ids: string | null;
    banner_config: any;
}

const { Form } = withTypes<BannerFormData>();

// Render helpers
const renderInput = ({
    meta: { touched, error } = { touched: false, error: undefined },
    input: { ...inputProps },
    label,
    ...props
}: any) => (
    <div>
        {label && <Typography variant="caption" style={{ color: '#666', marginBottom: 4, display: 'block' }}>{label}</Typography>}
        <TextField
            {...props}
            {...inputProps}
            error={!!(touched && error)}
            helperText={touched && error}
            fullWidth
            variant="outlined"
            size="small"
        />
    </div>
);

const renderSwitch = ({ input, label }: any) => {
    const isChecked = input.checked !== undefined ? input.checked : !!input.value;
    return (
        <FormControlLabel
            control={
                <Switch
                    checked={isChecked}
                    onChange={(event) => {
                        input.onChange(event.target.checked);
                    }}
                    color="primary"
                />
            }
            label={label}
        />
    );
};

const renderSelect = ({ input, label, options, ...props }: any) => (
    <div>
        {label && <Typography variant="caption" style={{ color: '#666', marginBottom: 4, display: 'block' }}>{label}</Typography>}
        <FormControl fullWidth variant="outlined" size="small">
            <Select
                value={input.value || ""}
                onChange={(e) => input.onChange(e.target.value)}
                onBlur={input.onBlur}
                {...props}
            >
                {options.map((opt: any) => (
                    <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                ))}
            </Select>
        </FormControl>
    </div>
);

const PromoBannerConfig: React.FC = () => {
    const classes = useStyles();
    const notify = useNotify();

    const [loading, setLoading] = useState(false);
    const [banners, setBanners] = useState<BannerRecord[]>([]);
    const [selectedBanner, setSelectedBanner] = useState<BannerRecord | null>(null);
    const [isEditing, setIsEditing] = useState(false);
    const [isCreating, setIsCreating] = useState(false);
    const [initialValues, setInitialValues] = useState<BannerFormData>({});
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [bannerToDelete, setBannerToDelete] = useState<BannerRecord | null>(null);

    useEffect(() => {
        loadBanners();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const loadBanners = async () => {
        setLoading(true);
        try {
            const config = getConfigGet({});
            const response: any = await axios.get(PROMO_BANNER_ENDPOINT, config);
            const data = response.data?.data || response.data || [];
            setBanners(Array.isArray(data) ? data : []);
        } catch (error) {
            console.error("Failed to load banners:", error);
            notify("Failed to load banners", { type: "error" });
        }
        setLoading(false);
    };

    const handleCreateNew = () => {
        setSelectedBanner(null);
        setIsCreating(true);
        setIsEditing(true);
        setInitialValues({
            is_active: false,
            priority: "0",
            target_user_type: "all",
            target_user_ids: "",
            banner_height: "150",
            banner_width: "350",
            click_action: "OPEN_REFERRAL_SHEET",
        });
    };

    const handleEditBanner = (banner: BannerRecord) => {
        setSelectedBanner(banner);
        setIsCreating(false);
        setIsEditing(true);

        const bannerConfig = banner.banner_config || {};
        // Support both old format (click_action) and new format (on_click.action)
        const onClick = bannerConfig.on_click || {};

        setInitialValues({
            name: banner.name,
            is_active: banner.is_active,
            priority: String(banner.priority || 0),
            target_user_type: banner.target_user_type || "all",
            target_user_ids: banner.target_user_ids || "",
            banner_image: bannerConfig.image || "",
            banner_height: String(bannerConfig.height || 150),
            banner_width: String(bannerConfig.width || 350),
            click_action: onClick.action || bannerConfig.click_action || "OPEN_REFERRAL_SHEET",
            click_url: onClick.url || bannerConfig.click_url || "",
        });
    };

    const handleCancelEdit = () => {
        setIsEditing(false);
        setIsCreating(false);
        setSelectedBanner(null);
    };

    const handleDeleteClick = (banner: BannerRecord) => {
        setBannerToDelete(banner);
        setDeleteDialogOpen(true);
    };

    const handleDeleteConfirm = async () => {
        if (!bannerToDelete) return;

        try {
            const headers = getConfigPost();
            await axios.delete(`${PROMO_BANNER_ENDPOINT}/${bannerToDelete.id}`, { headers });
            notify("Banner deleted successfully", { type: "success" });
            setDeleteDialogOpen(false);
            setBannerToDelete(null);
            loadBanners();
        } catch (error) {
            console.error("Failed to delete banner:", error);
            notify("Failed to delete banner", { type: "error" });
        }
    };

    const handleSubmit = async (values: BannerFormData) => {
        setLoading(true);

        // Build banner_config JSON with on_click structure
        const bannerConfig = {
            image: values.banner_image || null,
            height: parseInt(values.banner_height || "150"),
            width: parseInt(values.banner_width || "350"),
            on_click: {
                action: values.click_action || "OPEN_REFERRAL_SHEET",
                url: (values.click_action === "OPEN_WEBVIEW" || values.click_action === "OPEN_URL") ? (values.click_url || null) : null,
                screen_name: null,
            },
        };

        // Normalize target_user_ids: trim, dedup, keep only positive integers.
        // Only send it when target_user_type === 'specific_user_ids', else NULL.
        let normalizedIds: string | null = null;
        if (values.target_user_type === "specific_user_ids") {
            const raw = (values.target_user_ids || "").trim();
            const parts = raw.split(",")
                .map(s => s.trim())
                .filter(Boolean)
                .map(s => parseInt(s, 10))
                .filter(n => Number.isFinite(n) && n > 0);
            if (parts.length === 0) {
                notify("Please enter at least one valid user ID (comma-separated)", { type: "warning" });
                setLoading(false);
                return;
            }
            normalizedIds = Array.from(new Set(parts)).join(",");
        }

        const payload = {
            name: values.name,
            is_active: values.is_active || false,
            priority: parseInt(values.priority || "0"),
            target_user_type: values.target_user_type || "all",
            target_user_ids: normalizedIds,
            banner_config: bannerConfig,
        };

        try {
            const headers = getConfigPost();

            if (isCreating) {
                await axios.post(PROMO_BANNER_ENDPOINT, payload, { headers });
                notify("Banner created successfully!", { type: "success" });
            } else if (selectedBanner) {
                await axios.patch(`${PROMO_BANNER_ENDPOINT}/${selectedBanner.id}`, payload, { headers });
                notify("Banner updated successfully!", { type: "success" });
            }

            setIsEditing(false);
            setIsCreating(false);
            setSelectedBanner(null);
            loadBanners();
        } catch (error: any) {
            console.error("Failed to save banner:", error);
            notify(error.response?.data?.message || "Failed to save banner", { type: "error" });
        }

        setLoading(false);
    };

    // Click action options
    const clickActionOptions = [
        { value: "OPEN_REFERRAL_SHEET", label: "Open Referral Sheet" },
        { value: "OPEN_MORTGAGE_SHEET", label: "Open Mortgage Sheet" },
        { value: "OPEN_WALLET_TOP_SHEET", label: "Open Wallet Top Sheet" },
        { value: "NAVIGATE", label: "Navigate to Screen" },
        { value: "OPEN_URL", label: "Open URL (External Browser)" },
        { value: "OPEN_WEBVIEW", label: "Open Webview (In-App)" },
        { value: "NAVIGATE_DASHBOARD", label: "Go to Dashboard Tab" },
        { value: "NAVIGATE_AUTOSAVE", label: "Go to Autosave Tab" },
        { value: "NAVIGATE_SHOPPING", label: "Go to Shopping Tab" },
        { value: "NAVIGATE_LIVEDEAL", label: "Go to Live Deal Tab" },
        { value: "NAVIGATE_PRIZE", label: "Go to Prize Tab" },
    ];

    const targetUserOptions = [
        { value: "all", label: "All Users" },
        { value: "sprive_plus", label: "Sprive Plus Only" },
        { value: "non_sprive_plus_before_7_days", label: "Non-Sprive Plus (Before 7 Days)" },
        { value: "non_sprive_plus_after_7_days", label: "Non-Sprive Plus (After 7 Days)" },
        { value: "ambassador", label: "Ambassadors Only" },
        { value: "specific_user_ids", label: "Specific User IDs (Testing)" },
    ];

    const getTargetLabel = (value: string) => {
        const option = targetUserOptions.find(o => o.value === value);
        return option ? option.label : value;
    };

    if (loading && banners.length === 0) {
        return (
            <div className={classes.main} style={{ justifyContent: "center", alignItems: "center" }}>
                <CircularProgress />
            </div>
        );
    }

    return (
        <div className={classes.main}>
            <Notification />

            <div className={classes.header}>
                <Typography variant="h5" className={classes.title}>
                    Promo Banners
                </Typography>
                {!isEditing && (
                    <Button
                        variant="contained"
                        color="primary"
                        startIcon={<AddIcon />}
                        onClick={handleCreateNew}
                    >
                        Create New Banner
                    </Button>
                )}
            </div>

            {/* Banner List */}
            {!isEditing && (
                <div className={classes.tableContainer}>
                    <Table>
                        <TableHead>
                            <TableRow>
                                <TableCell>Name</TableCell>
                                <TableCell>Status</TableCell>
                                <TableCell>Priority</TableCell>
                                <TableCell>Target Audience</TableCell>
                                <TableCell>Actions</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {banners.map((banner) => (
                                <TableRow key={banner.id}>
                                    <TableCell>{banner.name}</TableCell>
                                    <TableCell>
                                        <Chip
                                            label={banner.is_active ? "Active" : "Inactive"}
                                            size="small"
                                            className={`${classes.statusChip} ${banner.is_active ? classes.activeChip : classes.inactiveChip}`}
                                        />
                                    </TableCell>
                                    <TableCell>{banner.priority}</TableCell>
                                    <TableCell>{getTargetLabel(banner.target_user_type)}</TableCell>
                                    <TableCell>
                                        <IconButton size="small" onClick={() => handleEditBanner(banner)}>
                                            <EditIcon />
                                        </IconButton>
                                        <IconButton size="small" onClick={() => handleDeleteClick(banner)}>
                                            <DeleteIcon />
                                        </IconButton>
                                    </TableCell>
                                </TableRow>
                            ))}
                            {banners.length === 0 && (
                                <TableRow>
                                    <TableCell colSpan={5} align="center">
                                        No banners found. Click "Create New Banner" to add one.
                                    </TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                </div>
            )}

            {/* Edit/Create Form */}
            {isEditing && (
                <Paper className={classes.formContainer}>
                    <Typography variant="h6" style={{ marginBottom: 24 }}>
                        {isCreating ? "Create New Banner" : "Edit Banner"}
                    </Typography>

                    <Form
                        onSubmit={handleSubmit}
                        initialValues={initialValues}
                        render={({ handleSubmit, values }) => (
                            <form onSubmit={handleSubmit}>
                                {/* Basic Info */}
                                <Typography className={classes.sectionTitle}>Basic Info</Typography>
                                <div className={classes.fieldGrid}>
                                    <Field name="name" component={renderInput} label="Banner Name" placeholder="Referral Promo Banner" />
                                    <Field name="priority" component={renderInput} label="Priority" type="number" placeholder="100" />
                                    <Field name="is_active" component={renderSwitch} label="Active" type="checkbox" />
                                    <Field name="target_user_type" component={renderSelect} label="Target Audience" options={targetUserOptions} />
                                </div>

                                {/* Specific user IDs - only shown when that targeting mode is selected */}
                                {values.target_user_type === "specific_user_ids" && (
                                    <div style={{ marginBottom: 24 }}>
                                        <Field
                                            name="target_user_ids"
                                            component={renderInput}
                                            label="Target User IDs (comma-separated)"
                                            placeholder="e.g. 12345, 67890, 11223"
                                            multiline
                                            rows={2}
                                        />
                                        <Typography variant="caption" style={{ color: '#666', marginTop: 4, display: 'block' }}>
                                            Only these specific users will see the banner. Use for production testing. Non-numeric or blank entries are ignored on save; empty list = no one matches.
                                        </Typography>
                                    </div>
                                )}

                                {/* Banner Config */}
                                <Typography className={classes.sectionTitle}>Banner Config</Typography>
                                <div className={classes.fieldGrid}>
                                    <Field name="banner_image" component={renderInput} label="Image URL" placeholder="https://cdn.sprive.com/banner.png" />
                                    <Field name="banner_height" component={renderInput} label="Height (px)" type="number" placeholder="150" />
                                    <Field name="banner_width" component={renderInput} label="Width (px)" type="number" placeholder="350" />
                                </div>

                                {/* Image Preview */}
                                {values.banner_image && (
                                    <div className={classes.previewBox}>
                                        <Typography variant="caption" style={{ display: 'block', marginBottom: 8 }}>Preview:</Typography>
                                        <img
                                            src={values.banner_image}
                                            alt="Banner preview"
                                            className={classes.previewImage}
                                            style={{
                                                width: values.banner_width ? `${values.banner_width}px` : 'auto',
                                                height: values.banner_height ? `${values.banner_height}px` : 'auto',
                                            }}
                                            onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
                                        />
                                    </div>
                                )}

                                {/* Click Action */}
                                <Typography className={classes.sectionTitle}>Click Action</Typography>
                                <div className={classes.fieldGrid}>
                                    <Field name="click_action" component={renderSelect} label="On Click Action" options={clickActionOptions} />
                                    {(values.click_action === "OPEN_WEBVIEW" || values.click_action === "OPEN_URL") && (
                                        <Field name="click_url" component={renderInput} label="URL" placeholder="https://sprive.com/promo" />
                                    )}
                                </div>

                                {/* Submit/Cancel Buttons */}
                                <div className={classes.buttonGroup}>
                                    <Button
                                        type="submit"
                                        variant="contained"
                                        color="primary"
                                        disabled={loading}
                                        style={{ marginRight: 16 }}
                                    >
                                        {loading ? <CircularProgress size={24} /> : (isCreating ? "Create Banner" : "Save Changes")}
                                    </Button>
                                    <Button
                                        variant="outlined"
                                        onClick={handleCancelEdit}
                                        disabled={loading}
                                    >
                                        Cancel
                                    </Button>
                                </div>
                            </form>
                        )}
                    />
                </Paper>
            )}

            {/* Delete Confirmation Dialog */}
            <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
                <DialogTitle>Delete Banner</DialogTitle>
                <DialogContent>
                    Are you sure you want to delete "{bannerToDelete?.name}"? This action cannot be undone.
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
                    <Button onClick={handleDeleteConfirm} color="secondary" variant="contained">
                        Delete
                    </Button>
                </DialogActions>
            </Dialog>
        </div>
    );
};

export default PromoBannerConfig;
